import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { passwordsMatchValidator } from '../../shared/validators/password-match.validator';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  email = '';
  token = '';
  newPassword = '';
  confirmPassword = '';
  message = '';
  loading = false;
  validToken = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  isSubmitted = false;

  resetForm = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ],
      ],
    },
    {
      validators: passwordsMatchValidator(),
    }
  );

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private auth: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (this.token) {
      this.auth
        .verifyResetToken(this.token)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => (this.validToken = true),
          error: () => this.toastr.error(this.translate.instant('PASSWORD_RESET_EXPIRED_LINK')),
        });
    } else {
      this.toastr.error(this.translate.instant('PASSWORD_RESET_EXPIRED_LINK'));
    }
  }

  resetPassword() {
    this.isSubmitted = true;

    if (this.newPassword !== this.confirmPassword) {
      this.toastr.error(this.translate.instant('PASSWORD_RESET_NOT_MATCH'));
      return;
    }

    this.loading = true;
    this.auth
      .resetPassword(this.token, this.passwordValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('PASSWORD_RESET_SUCCESS'));
          this.loading = false;
          setTimeout(() => this.router.navigate(['/login']), 3000);
        },
        error: () => {
          this.toastr.error(this.translate.instant('PASSWORD_RESET_FAIL'));
          this.loading = false;
        },
      });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControls() {
    return this.resetForm.controls;
  }

  get passwordValue(): string {
    return this.resetForm.get('password')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= 6;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }

  get hasNumber(): boolean {
    return /\d/.test(this.passwordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*]/.test(this.passwordValue);
  }

  get passwordControl() {
    return this.resetForm.get('password');
  }
  get confirmPasswordControl() {
    return this.resetForm.get('confirmPassword');
  }
}
